import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { userApi } from "../../api/http";
import { getContextualError, logError } from "../../utils/errorHandling";
import { validatePassword } from "../../utils/passwordValidation";
import { scrollToError } from "../../utils/scrollToError";
import PasswordRequirementsList from "./PasswordRequirementsList";
import type { components } from "../../api/types.gen";
import authStyles from "./AuthForm.module.css";
import styles from "./ResetPassword.module.css";

// Request payload for POST /api/auth/reset-password, sourced from the
// generated OpenAPI contract rather than hand-duplicated. The 200 response
// carries no body (see operations.postApiAuthResetPassword), so the userApi
// call is typed to resolve to `void`.
type ResetPasswordBody = components["schemas"]["ResetPasswordBody"];

const ResetPassword = (): React.JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture the reset token from the URL exactly once, on first render. The
  // token is a bearer capability: left in the address bar it persists in
  // browser history, is re-sent on every reload, can leak via the Referer
  // header on any outbound request, and is visible to anything reading
  // window.location (including logError). We strip it from the URL below and
  // submit the copy held here instead.
  const [token] = useState<string | null>(() => searchParams.get("token"));
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  // Remove the token from the address bar as soon as it has been captured
  // above. Uses `replace` so the token never becomes a history entry. The
  // `has("token")` guard makes this a no-op once stripped, so re-running the
  // effect (after setSearchParams updates `searchParams`) cannot loop.
  useEffect(() => {
    if (!searchParams.has("token")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("token");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must contain: ${passwordErrors.join(", ")}`);
      setLoading(false);
      scrollToError(`.${authStyles["error-message"]}`);
      return;
    }

    try {
      // The form is only reachable when `token` is present (the missing-token
      // branch renders no form), so this is always the real token at runtime;
      // the `?? ""` only satisfies the contract's `string` for the null type.
      const body: ResetPasswordBody = { newPassword, token: token ?? "" };
      await userApi<void>("/api/auth/reset-password", {
        method: "POST",
        body,
        suppress401Handler: true,
      });
      setSuccess(true);
    } catch (err) {
      logError(err, "client_auth", "Reset password");
      setError(
        getContextualError(
          err,
          undefined,
          "Reset failed — the link may have expired. Request a new reset email.",
        ),
      );
      scrollToError(`.${authStyles["error-message"]}`);
    }

    setLoading(false);
  };

  const passwordStrength = validatePassword(newPassword);
  const isPasswordValid = passwordStrength.length === 0;

  return (
    <div className={styles["reset-password-container"]}>
      <div className={styles["reset-password-card"]}>
        <h1>Reset Password</h1>

        {!token ? (
          <div className={authStyles["error-message"]}>
            This link is missing its reset token. Request a new password reset
            email and use the link it contains.
          </div>
        ) : success ? (
          <div className={styles["reset-password-success"]}>
            <p>Password reset. You can now sign in with your new password.</p>
            <Link to="/login" className={authStyles["text-btn"]}>
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={styles["reset-password-form"]}
          >
            {error && (
              <div className={authStyles["error-message"]}>{error}</div>
            )}

            <div className={authStyles["form-group"]}>
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
              />

              {newPassword && (
                <PasswordRequirementsList password={newPassword} />
              )}
            </div>

            <div className={authStyles["form-group"]}>
              <label htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="new-password"
              />

              {confirmPassword && newPassword !== confirmPassword && (
                <div className={authStyles["password-mismatch"]}>
                  Passwords do not match
                </div>
              )}
            </div>

            <div className={authStyles["form-group"]}>
              <label className={authStyles["show-passwords-checkbox"]}>
                <input
                  type="checkbox"
                  checked={showPasswords}
                  onChange={(e) => setShowPasswords(e.target.checked)}
                  disabled={loading}
                />
                Show passwords
              </label>
            </div>

            <button
              type="submit"
              disabled={
                loading || !isPasswordValid || newPassword !== confirmPassword
              }
              className={authStyles["submit-btn"]}
            >
              {loading ? "Resetting…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
